import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  STAFF_LIMITS,
  STAFF_PASSWORD_POLICY,
  checkStaffPasswordPolicy,
} from '@icebreaker/shared/staff';
import { Button, Field, Input, PasswordRuleList, fieldStyles, useFormErrors } from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { type RootState } from '@/shared/store/store';
import { changeStaffPassword, passwordFormReset } from '../store/staff-access.slice';
import styles from './staff-access.module.css';

/**
 * PERSONEL ŞİFRESİNİ DEĞİŞTİRME FORMU.
 *
 * İLK KURULUM ARTIK BU FORMDAN GEÇMEZ (panel kimliği ayrımı · Karar 4c): ilk
 * şifre davet jetonuyla `SetupScreen`de kurulur ve o ekran jeton alanını da
 * taşır. Bu form yalnız "mevcut şifre + yeni şifre" yolunu sürer; `requireCurrent`
 * parametresi bu yüzden KALDIRILDI — mevcut şifre her zaman zorunludur.
 *
 * ALAN BAZLI HATA: sunucu 400'de `fieldErrors[]` döner ve cümle ALANIN ALTINA
 * yazılır (`useFormErrors`) — "Girdiğin bilgilerde hata var" ile geçiştirmek
 * bu depoda ölçülmüş bir kusurdur.
 *
 * TEKRAR ALANI İSTEMCİDEDİR: sunucuya gitmez, çünkü sunucunun doğrulayacağı
 * bir şey değildir (yazım hatası kontrolü). Cümle de istemcide üretilir.
 */
export function PasswordForm({
  idPrefix,
  submitLabel,
  secondaryAction,
  onSaved,
}: {
  readonly idPrefix: string;
  readonly submitLabel: string;
  readonly secondaryAction?: ReactNode;
  /** Kaydetme başarıya ulaştığında çağrılır (diyalog onay cümlesine geçer). */
  readonly onSaved?: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const save = useAppSelector((state: RootState) => state.staffAccess.saveState);
  const saved = useAppSelector((state: RootState) => state.staffAccess.saved);
  const formErrors = useFormErrors(save.error, INLINE_ERROR_PATHS);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  useEffect(() => {
    if (saved) onSaved?.();
  }, [saved, onSaved]);

  // Form açılırken önceki denemenin hatası/onayı taşınmaz.
  useEffect(() => {
    dispatch(passwordFormReset());
    return () => {
      dispatch(passwordFormReset());
    };
  }, [dispatch]);

  const mismatch = repeat.length > 0 && repeat !== next;
  const canSubmit =
    checkStaffPasswordPolicy(next).ok &&
    !mismatch &&
    repeat.length > 0 &&
    current.length > 0 &&
    save.status !== 'loading';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    dispatch(changeStaffPassword.request({ currentPassword: current, newPassword: next }));
  };

  return (
    <form onSubmit={submit} className={styles.form}>
      <Field
        id={`${idPrefix}-current`}
        label={t.t('admin.panel.password.currentLabel')}
        error={formErrors.at('currentPassword')}
      >
        <Input
          id={`${idPrefix}-current`}
          name="currentPassword"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          invalid={Boolean(formErrors.at('currentPassword'))}
          required
        />
      </Field>
      <Field
        id={`${idPrefix}-new`}
        label={t.t('admin.panel.password.newLabel')}
        hint={t.t('admin.panel.password.hint')}
        error={formErrors.at('newPassword')}
      >
        <Input
          id={`${idPrefix}-new`}
          name="newPassword"
          type="password"
          autoComplete="new-password"
          minLength={STAFF_LIMITS.passwordMinLength}
          maxLength={STAFF_LIMITS.passwordMaxLength}
          value={next}
          onChange={(event) => setNext(event.target.value)}
          invalid={Boolean(formErrors.at('newPassword'))}
          aria-describedby={`${idPrefix}-new-rules`}
          required
        />
        <PasswordRuleList
          id={`${idPrefix}-new-rules`}
          password={next}
          policy={STAFF_PASSWORD_POLICY}
        />
      </Field>
      <Field
        id={`${idPrefix}-repeat`}
        label={t.t('admin.panel.password.repeatLabel')}
        error={mismatch ? t.t('admin.panel.password.repeatMismatch') : undefined}
      >
        <Input
          id={`${idPrefix}-repeat`}
          name="repeatPassword"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          invalid={mismatch}
          required
        />
      </Field>
      {/* Özet cümleler ÇEVRİLMİŞTİR; dikişe verilmez (error-message.guard KURAL 5b). */}
      {formErrors.summary.map((sentence) => (
        <p key={sentence} className={fieldStyles.error} role="alert">
          {sentence}
        </p>
      ))}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={!canSubmit}>
          {submitLabel}
        </Button>
        {secondaryAction}
      </div>
    </form>
  );
}

/** Cümlesi ALANIN ALTINA yazılan sunucu yolları. */
const INLINE_ERROR_PATHS = ['currentPassword', 'newPassword'];
